import { oc } from '@orpc/contract';
import { z } from 'zod';
import { paginatedSchema, paginationQuerySchema } from './pagination';

/**
 * Labor Core 行政申請確認一覧（Package ネスト）。
 * Backend: list-egov-application-steps-by-tag.api-contract.ts
 *
 * `@standandforce/api-contract` に `correctionSubmissionMode` 取り込み後は
 * パッケージ export へ差し替え、本ファイルを削除する。
 */

const OperationEngineTargetSchema = z.object({
  targetType: z.enum(['Person', 'Device', 'AppIntegration', 'Contract', 'Software']),
  targetId: z.string(),
  targetName: z.string(),
  targetAvatarUrl: z.string().optional()
});
const egovApplicationConfirmationListStatusValues = ['notApplied', 'submitFailed', 'amendFailed', 'withdrawFailed', 'submitting', 'amending', 'withdrawInProgress', 'syncing', 'syncFailed', 'withdrawing', 'withdrawn', 'returned', 'correctionPending', 'completed', 'inProgress'] as const;
const governmentApplicationStepClassificationCodeValues = ['SOCIAL_INSURANCE', 'LABOR_INSURANCE', 'UNKNOWN'] as const;
const operationTaskStatusValues = ['NotStarted', 'InProgress', 'Completed', 'WaitingApproval', 'Abort', 'Skipped'] as const;
const EgovApplicationConfirmationListStatusSchema = z.enum(egovApplicationConfirmationListStatusValues);
const GovernmentApplicationStepClassificationCodeSchema = z.enum(governmentApplicationStepClassificationCodeValues);
const OperationTaskStatusSchema = z.enum(operationTaskStatusValues);
const EgovOfficialDocumentSchema = z.object({
  originalName: z.string(),
  s3Url: z.string()
});
const displayMessagesSchema = z.object({
  en: z.string(),
  ja: z.string(),
  id: z.string(),
  vn: z.string(),
  th: z.string(),
  zhCN: z.string()
});
const EgovFailureErrorSchema = z.object({
  displayMessages: displayMessagesSchema
});
const EgovActiveNoticeSchema = z.object({
  noticeSubId: z.number(),
  noticeTitle: z.string(),
  noticeSentence: z.string().nullable(),
  correctType: z.string().nullable(),
  correctExpiredDate: z.string().nullable(),
  correctCompletedDate: z.string().nullable(),
  issueDate: z.string(),
  noticeDataType: z.string(),
  attachedFiles: z.array(EgovOfficialDocumentSchema)
});

/**
 * 補正待ち／返戻時の送信種別（未完了補正通知の correctType + サブステータスフォールバック）。
 * Backend wire: camelCase（domain の partial_correction とは別）。
 */
const EgovCorrectionSubmissionModeSchema = z.enum(['partialCorrection', 'resubmission']);
const ListEgovApplicationStepsByTagStepRowSchema = z.object({
  operationStepInstanceId: z.string(),
  target: OperationEngineTargetSchema,
  stepName: z.string(),
  classificationCode: GovernmentApplicationStepClassificationCodeSchema.nullable(),
  classificationName: z.string().nullable(),
  dueDate: z.string().nullable(),
  status: EgovApplicationConfirmationListStatusSchema,
  applicationSubmittedAt: z.string().nullable(),
  arriveId: z.string().nullable(),
  lastSyncedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  operationTaskInstanceId: z.string().nullable(),
  operationTaskStatus: OperationTaskStatusSchema.nullable(),
  govProfileId: z.string().nullable(),
  isCorrectionPending: z.boolean(),
  /**
   * 補正待ち／返戻のとき部分補正か再提出か。それ以外・判定不能は null。
   */
  correctionSubmissionMode: EgovCorrectionSubmissionModeSchema.nullable(),
  officialDocuments: z.array(EgovOfficialDocumentSchema),
  activeNotices: z.array(EgovActiveNoticeSchema),
  failureError: EgovFailureErrorSchema.nullable()
});
const ListEgovApplicationStepsByTagPackageGroupSchema = z.object({
  operationPackageInstanceId: z.string(),
  target: OperationEngineTargetSchema,
  canArchive: z.boolean(),
  steps: z.array(ListEgovApplicationStepsByTagStepRowSchema)
});
const ListEgovApplicationStepsByTagOutputSchema = paginatedSchema(ListEgovApplicationStepsByTagPackageGroupSchema);
export type ListEgovApplicationStepsByTagStepRowOutput = z.infer<typeof ListEgovApplicationStepsByTagStepRowSchema>;
export type ListEgovApplicationStepsByTagOutput = z.infer<typeof ListEgovApplicationStepsByTagOutputSchema>;
const listOrderFields = z.enum(['target', 'classification', 'stepName', 'status', 'arriveId', 'applicationSubmittedAt', 'dueDate', 'lastSyncedAt']);
const ListEgovApplicationStepsByTagInputSchema = paginationQuerySchema(listOrderFields).extend({
  tagId: z.string(),
  /** 一覧の対象を限定する OperationPackagePattern。同名タグが複数パターンで再利用され得るため必須 */
  operationPackagePatternId: z.string(),
  orderDirection: z.enum(['asc', 'desc']).default('asc'),
  pageSize: z.coerce.number().int().min(1).max(2000).default(50),
  searchText: z.string().max(500).optional(),
  status: z.array(EgovApplicationConfirmationListStatusSchema).optional()
});
export const listEgovApplicationStepsByTagContract = oc.route({
  method: 'GET',
  path: '/v2/boe/packages/HRF_LABOR_CORE/instance-tags/{tagId}/egov-confirmation-steps'
}).input(ListEgovApplicationStepsByTagInputSchema).output(ListEgovApplicationStepsByTagOutputSchema);
export type ListEgovApplicationStepsByTagInput = z.infer<typeof ListEgovApplicationStepsByTagInputSchema>;
const ListEgovApplicationStepsByPackageInstancesInputSchema = z.object({
  page: z.coerce.number().int().min(1).optional(),
  pageSize: z.coerce.number().int().min(1).max(2000).default(50),
  orderBy: listOrderFields.optional(),
  orderDirection: z.enum(['asc', 'desc']).default('asc'),
  operationPackageInstanceIds: z.array(z.string()).min(1).max(200),
  searchText: z.string().max(500).optional(),
  status: z.array(EgovApplicationConfirmationListStatusSchema).optional()
});
export const listEgovApplicationStepsByPackageInstancesContract = oc.route({
  method: 'GET',
  path: '/v2/boe/packages/HRF_LABOR_CORE/egov-confirmation-steps/by-package-instances'
}).input(ListEgovApplicationStepsByPackageInstancesInputSchema).output(ListEgovApplicationStepsByTagOutputSchema);
export type ListEgovApplicationStepsByPackageInstancesInput = z.infer<typeof ListEgovApplicationStepsByPackageInstancesInputSchema>;